import moment from "moment";
import { useCallback, useState } from "react";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { Link } from "react-router-dom";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import Icon from "../../../components/icon/Icon";
import { Loader } from "../../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import CustomTable from "../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useExpirationManagementStates from "../../../hooks/api-calls/expirations/useExpirationManagementStates";
import useExpirationStates from "../../../hooks/api-calls/expirations/useExpirationStates";
import useKoReasons from "../../../hooks/api-calls/useKoReasons";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import Page from '../../../layout/Page/Page';
import { menuRoutes } from "../../../router/menu";
import { ExpirationService } from "../../../services/expiration/expirationService";
import { ExpirationApiResponse } from "../../../type/expiration-type";
import ExpirationFilters from "./ExpirationFilters";

interface ListExpirationsProps { }

const columnOrder = [
    'id',
    'currentDueStatus',
    'currentDueStatusDate',
    'currentDueManagementStatus',
    'currentDueManagementStatusDate',
    'description',
    'invoice',
    'contract',
    'expirationDate',
    'paymentMethod',
    'amount',
    'reminder',
    'reminderDate',
    'reminderResponsible',
    'notes',
    'paymentDate',
    'updatedAt',
    'createdAt',
    'isDeleted',
];

const ListExpirations: React.FC<ListExpirationsProps> = ({ }) => {

    // HOOKS

    const service = new ExpirationService();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getExpirationStatesList } = useExpirationStates();
    const { getExpirationManagementStatesList } = useExpirationManagementStates();
    const { koReasonsOptions } = useKoReasons();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoadingXLSX, setIsLoadingXLSX] = useState<boolean>(false);
    const [isLoadingCSV, setIsLoadingCSV] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listExpirations(filters);
        return response.getResponseData() as ExpirationApiResponse;
    }, [filters]));

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR VENCIMIENTO
     * @EN DELETE EXPIRATION
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteExpiration(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Vencimiento eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MÚLTIPLES VENCIMIENTOS
     * @EN DELETE MULTIPLE EXPIRATIONS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiExpirations(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Vencimientos eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO
     * @EN CHANGE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, entityName: string, koReasonId?: string) => {
        var response = { success: false };
        switch (entityName) {
            case 'expiration':
                try {
                    response = await (await service.changeExpirationStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del vencimiento actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'expiration_management':
                try {
                    response = await (await service.changeExpirationManagementStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de gestión de vencimientos actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            default:
                break;
        }
        refetch();
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR VENCIMIENTOS A XLSX
     * @EN EXPORT EXPIRATIONS TO XLSX
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleExportXLSX = async (ids: string[]) => {
        setIsLoadingXLSX(true);
        try {
            const response = (await service.exportExpirationsXLSX(ids));
            if (response) {
                const fileData = response.getResponseData();
                const blob = new Blob([fileData]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `vencimientos_${moment().format('DD-MM-YYYY')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('Error al exportar los vencimientos a excel');
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoadingXLSX(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR VENCIMIENTOS A CSV
     * @EN EXPORT EXPIRATIONS TO CSV
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleExportCSV = async (ids: string[]) => {
        setIsLoadingCSV(true);
        try {
            const response = (await service.exportExpirationsCSV(ids));
            if (response) {
                const blob = new Blob([response.getResponseData()], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `vencimientos_${moment().format('DD-MM-YYYY')}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('Error al exportar los vencimientos a CSV');
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoadingCSV(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id={'incidence-types-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['isDeleted', 'currentDueStatusDate', 'currentDueManagementStatusDate', 'reminderDate', 'reminderResponsible']}
                    overrideColumns={[
                        {
                            key: 'currentDueStatus',
                            render: (row: any) => {
                                const statusDate = row.row.original?.currentDueStatusDate?.date;
                                const dueStatus = row.row.original?.currentDueStatus;
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                        {(statusDate && dueStatus)
                                            ? (
                                                <StatusDropdown
                                                    disabled={!userCan('change_expiration_statuses', 'expirations')}
                                                    entityId={row.row.original?.id}
                                                    title="¿Estás seguro de cambiar el estado del vencimiento?"
                                                    statesOptions={getExpirationStatesList(dueStatus.id)}
                                                    currentState={dueStatus}
                                                    currentStateDate={statusDate}
                                                    koReasonsOptions={koReasonsOptions}
                                                    koReasonStatusEntity="expiration"
                                                    handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                        handleStateChange(entityId, statusId, comment, 'expiration', koReasonId)
                                                    }
                                                />
                                            )
                                            : "N/A"
                                        }
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'currentDueManagementStatus',
                            render: (row: any) => {
                                const statusDate = row.row.original?.currentDueManagementStatusDate?.date;
                                const dueManagementStatus = row.row.original?.currentDueManagementStatus;
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                        {(statusDate && dueManagementStatus)
                                            ? (
                                                <StatusDropdown
                                                    disabled={!userCan('change_expiration_management_statuses', 'expirations')}
                                                    entityId={row.row.original?.id}
                                                    title="¿Estás seguro de cambiar el estado de gestión del vencimiento?"
                                                    statesOptions={getExpirationManagementStatesList(dueManagementStatus.id)}
                                                    currentState={dueManagementStatus}
                                                    currentStateDate={statusDate}
                                                    koReasonsOptions={koReasonsOptions}
                                                    koReasonStatusEntity="expiration_management"
                                                    handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                        handleStateChange(entityId, statusId, comment, 'expiration_management', koReasonId)
                                                    }
                                                />
                                            )
                                            : "N/A"
                                        }
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'invoice',
                            render: (row: any) => {
                                return (
                                    <Link to={`${menuRoutes.invoices.path}/${row.row.original?.invoice?.id}/profile/info`} className="font-bold text-primary">
                                        {row.row.original?.invoice?.number}
                                    </Link>
                                );
                            },
                        },
                        {
                            key: 'contract',
                            render: (row: any) => {
                                return (
                                    <Link to={`${menuRoutes.contracts.path}/${row.row.original?.contract?.id}/profile/info`} className="font-bold text-primary">
                                        {row.row.original?.contract?.code}
                                    </Link>
                                );
                            },
                        },
                        {
                            key: 'expirationDate',
                            render: (row: any) => {
                                const date = row.row.original?.expirationDate?.date;
                                return date ? new Date(date).toLocaleDateString('es-ES') : 'N/A';
                            },
                        },
                        {
                            key: 'paymentMethod',
                            render: (row: any) => {
                                return row.row.original?.paymentMethod?.name ? row.row.original?.paymentMethod?.name : 'N/A';
                            },
                        },
                        {
                            key: 'amount',
                            render: (row: any) => {
                                return row.row.original?.amount ? `${row.row.original?.amount} €` : 'N/A';
                            },
                        },
                        {
                            key: 'reminder',
                            render: (row: any) => {
                                const date = row.row.original?.reminderDate?.date;
                                const responsible = row.row.original?.reminderResponsible
                                    ? row.row.original?.reminderResponsible?.name + (row.row.original?.reminderResponsible?.lastName ? ` ${row.row.original?.reminderResponsible?.lastName}` : '')
                                    : null;
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center'>
                                        {date ? moment(date).format('DD/MM/YYYY') : 'N/A'}
                                        <Link to={`${menuRoutes.users.path}/${row.row.original?.reminderResponsible?.id}/profile/info`} className="font-bold text-primary">
                                            {responsible}
                                        </Link>
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'notes',
                            render: (row: any) => {
                                return (
                                    row.row.original?.notes
                                        ? (
                                            <Tooltip
                                                size="md"
                                                content={<div className='tooltip-container'>{row.row.original.notes}</div>}
                                                placement="top"
                                                color="invert"
                                            >
                                                <div>
                                                    <Icon icon="Comment" className="h6- w-6" />
                                                </div>
                                            </Tooltip>
                                        )
                                        : 'N/A'
                                )
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'expirations', permission: 'edit_expirations' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar vencimiento'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.expirations.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'expirations', permission: 'delete_expirations' },
                            render: (row: any) => {
                                if (row.isDeleted) return null; // If the row is deleted, do not show the delete button
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar vencimiento`}
                                                description={`¿Estás seguro de que deseas eliminar el vencimiento?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    multiActions={[
                        {
                            label: 'Exportar XLSX',
                            permissions: { group: 'expirations', permission: 'export_expirations' },
                            render: (selectedRows: any) => {
                                return (
                                    <Button
                                        variant={'outline'}
                                        color={'primary'}
                                        size={'sm'}
                                        onClick={() => handleExportXLSX(selectedRows)}
                                        className='p-3 text-nowrap'
                                        isLoading={isLoadingXLSX}
                                        disabled={isLoadingXLSX}
                                    >
                                        Exportar XLSX
                                    </Button>
                                )
                            },
                        },
                        {
                            label: 'Exportar CSV',
                            permissions: { group: 'expirations', permission: 'export_expirations' },
                            render: (selectedRows: any) => {
                                return (
                                    <Button
                                        variant={'outline'}
                                        color={'primary'}
                                        size={'sm'}
                                        onClick={() => handleExportCSV(selectedRows)}
                                        className='p-3 text-nowrap'
                                        isLoading={isLoadingCSV}
                                        disabled={isLoadingCSV}
                                    >
                                        Exportar CSV
                                    </Button>
                                )
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'expirations', action: 'delete_expirations' }}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ExpirationApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            {!data?.data && loading && (<Loader height='40vh' />)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de vencimientos'}>
                <ExpirationFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default ListExpirations;